import { CommonModule } from '@angular/common';
import {
  Component,
  Inject,
  inject,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { MaterialModule } from '../../material/material.module';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheet,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { CandidateDetailsComponent } from '../../components/candidate-details/candidate-details.component';
import { CareerDetailsComponent } from '../../components/career-details/career-details.component';
import { CandidateAnalysisComponent } from '../../components/candidate-analysis/candidate-analysis.component';
import { AudioAnalysisComponent } from '../../components/audio-analysis/audio-analysis.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { VjsPlayerComponent } from '../../components/vjs-player/vjs-player.component';
import { InViewDirective } from '../../directives/in-view.directive';
import { JobsService } from '../../services/jobs.service';
import { finalize } from 'rxjs';
import {
  IStepOption,
  TourMatMenuModule,
  TourService,
} from 'ngx-ui-tour-md-menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppService } from '../../services/app.service';

@Component({
  selector: 'app-review',
  imports: [
    CommonModule,
    MaterialModule,
    RouterModule,
    CandidateDetailsComponent,
    CareerDetailsComponent,
    CandidateAnalysisComponent,
    AudioAnalysisComponent,
    VjsPlayerComponent,
    InViewDirective,
    TourMatMenuModule,
  ],
  templateUrl: './review.component.html',
  styleUrl: './review.component.scss',
})
export class ReviewComponent {
  private readonly _appService = inject(AppService);
  private readonly _bottomSheet = inject(MatBottomSheet);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _jobService = inject(JobsService);
  private readonly _tourService = inject(TourService);
  private readonly _snackBar = inject(MatSnackBar);

  reviewTaken: Signal<boolean> = this._appService.reviewTourTaken;

  jobId: WritableSignal<string> = signal('');
  candidateId: WritableSignal<string> = signal('');
  candidates: WritableSignal<any[]> = signal([]);
  visibleCandidates: WritableSignal<any[]> = signal([]);
  currentIndex: WritableSignal<number> = signal(-1);
  loader: WritableSignal<any> = signal({
    candidate: false,
    action: false
  });

  steps: IStepOption[] = [
    {
      anchorId: 'cad',
      title: 'Candidate Details',
      content:
        'View candidate basic information including education, nationality details',
    },
    {
      anchorId: 'crd',
      title: 'Career Details',
      content:
        'Professional journey timeline, skills progression, and key achievements',
    },
    {
      anchorId: 'ma',
      title: 'Match Analysis',
      content:
        'AI-powered evaluation of candidate fit, skills alignment, and role compatibility',
    },
    {
      anchorId: 'aa',
      title: 'Audio Analysis',
      content:
        'Interview audio insights, communication style, and key discussion points summary',
    },
    {
      anchorId: 'do',
      title: 'Download Options',
      content:
        'Access interview recordings and resume files. Click icon to download candidate materials',
    },
    {
      anchorId: 'ai',
      title: 'Action Items',
      content:
        'After evaluation, select an action from reject, hold and pass to next round',
    },
  ];

  constructor() {
    this._route.queryParams.subscribe((data: any) => {
      this.jobId.set(data?.jobID || '');
      if (!this.jobId()) {
        this._router.navigate(['/jobs']);
        this._route;
      } else {
        this.candidateId.set(data?.candidateID || '');
        this.getInterviews();
      }
    });
  }

  ngAfterViewInit(): void {
    this._tourService.initialize(this.steps, {
      enableBackdrop: true,

      backdropConfig: {
        offset: 10,
      },
    });

    const isMobile = window.innerWidth < 768;

    if (isMobile && !this.reviewTaken()) {
      this._tourService.start();
      this._tourService.end$.subscribe(() => {
        localStorage.setItem('reviewTourState', JSON.stringify('true'));
        this.currentIndex.set(0);
      });
    }
  }

  loadMore(event: { isLast: boolean; index: number }) {
    this.currentIndex.set(event.index);

    if (event.isLast) {
      if (this.currentIndex() >= this.candidates().length) return;
      const nextItem = this.candidates()[this.currentIndex() + 1];
      if (nextItem) {
        this.visibleCandidates.set([...this.visibleCandidates(), nextItem]);
        // this.changeDetectorRef.detectChanges();
      }
    }
  }

  getInterviews() {
    if (this.candidateId()) {
      this.toggleCandidateLoader();
      this._jobService
        .getCandidateById(this.jobId(), this.candidateId())
        .pipe(finalize(() => (this.toggleCandidateLoader())))
        .subscribe((candidate: any) => {
          this.candidates.set(candidate.data);
          this.visibleCandidates.set([this.candidates()[0]]);
        });
    } else {
      this.loader().candidate = true;
      this._jobService
        .getCandidatesForReview(this.jobId())
        .pipe(finalize(() => (this.toggleCandidateLoader())))
        .subscribe((candidates: any) => {
          if (candidates?.data?.length > 0) {
            this.candidates.set(candidates.data.filter(
              (item: any) => item?.status != 'pending' &&
                item?.status != 'analyzing'
            ));
            if (this.candidates().length > 0) {
              this.visibleCandidates.set([this.candidates()[0]]);
            }
          }
        });
    }
  }

  toggleCandidateLoader() {
    this.loader.update((item) => ({
      ...item,
      candidate: !this.loader().candidate
    }))
  }

  toggleActionLoader() {
    this.loader.update((item) => ({
      ...item,
      action: !this.loader().action
    }))
  }

  openBottomSheet(
    type:
      | 'candidate'
      | 'career'
      | 'candidate-analysis'
      | 'audio-analysis'
      | 'download'
  ): void {
    const candidate = this.visibleCandidates()[this.currentIndex()];
    switch (type) {
      case 'candidate':
        this._bottomSheet.open(CandidateDetailsComponent, {
          data: candidate,
        });
        break;
      case 'career':
        this._bottomSheet.open(CareerDetailsComponent, {
          data: candidate,
        });
        break;
      case 'candidate-analysis':
        this._bottomSheet.open(CandidateAnalysisComponent, {
          data: candidate,
        });
        break;
      case 'audio-analysis':
        this._bottomSheet.open(AudioAnalysisComponent, {
          data: candidate,
        });
        break;
      case 'download':
        this._bottomSheet.open(DownloadOptionsComponent, {
          data: candidate,
        });
        break;
      default:
        break;
    }
  }

  updateStatus(
    status:
      | 'pending'
      | 'rejected'
      | 'hold'
      | 'accepted'
      | 'analyzing'
      | 'pending review'
  ) {
    this.toggleActionLoader()
    this._jobService
      .updateReviewStatus(
        this.visibleCandidates()[this.currentIndex()]?.resume_analysis
          ?.candidate_id,
        status
      )
      .pipe(finalize(() => (this.toggleActionLoader())))
      .subscribe((res: any) => {
        this.visibleCandidates()[this.currentIndex()].status = status;
        this._snackBar.open(res.message);
      });
  }
}

@Component({
  selector: 'app-download',
  imports: [CommonModule, MaterialModule],
  template: `
    <div class="border-b border-gray-100 p-3 md:p-4">
      <mat-card-title class="!flex items-center justify-between w-full">
        <div class="flex items-center space-x-2">
          <span class="text-xl font-semibold text-gray-800">Options</span>
        </div>
      </mat-card-title>
    </div>
    <mat-nav-list>
      <button (click)="downloadResume(candidate.resume)" mat-list-item>
        <mat-icon matListItemIcon>description</mat-icon>
        <span matLine>Download Resume</span>
      </button>

      @if(candidate?.resume_analysis?.interview_video_url) {
      <button
        (click)="
          downloadResume(candidate?.resume_analysis?.interview_video_url)
        "
        mat-list-item
      >
        <mat-icon matListItemIcon>play_circle</mat-icon>
        <span matLine>Download Interview</span>
      </button>
      }
    </mat-nav-list>
  `,
})
export class DownloadOptionsComponent {
  constructor(@Inject(MAT_BOTTOM_SHEET_DATA) public candidate: any) { }

  private _bottomSheetRef =
    inject<MatBottomSheetRef<DownloadOptionsComponent>>(MatBottomSheetRef);

  close() {
    this._bottomSheetRef.dismiss();
  }

  downloadResume(link: string) {
    const a = document.createElement('a');
    a.href = link;
    a.download = 'resume.pdf';
    a.click();
  }
}
